import { Box, makeStyles, Typography } from "@material-ui/core";
import clsx from "clsx";
import Highcharts from "highcharts";
import { useState } from "react";
import { STAKING_TYPE } from "src/components/ScoringChart/Stack/config";
import StackChart from "src/components/ScoringChart/Stack/StackChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import TitleTypography from "src/components/Typography/TitleTypography";
import PaperWrapper from "src/components/Wrapper/PaperWrapper";
import { useReputationSelector } from "src/context/reputationContext";
import useNFTBalance from "src/hooks/useNFTBalance";
import useRTravaBalance from "src/hooks/useRTravaBalance";
import useVaultRewardBalance from "src/hooks/useVaultRewardBalance";
import { isNumeric, numberWithCommas, toFixed } from "src/services/utils";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";
import LockHoverButton from "../LockHoverButton";

const useStyle = makeStyles((theme) => ({
  root: {
    padding: "20px",
    position: "relative",
    minHeight: 300,
  },
  balance: {
    color: theme.palette.text.hint,
    fontSize: "16px",
    fontWeight: 500,
  },
  fogContent: {
    filter: "blur(4px)",
  },
}));

const getOption = () => ({
  chart: {
    height: 300,
  },
  xAxis: {
    type: "datetime",
    labels: {
      formatter: function () {
        return Highcharts.dateFormat("%d %b '%y", this.value);
      },
    },
  },
  yAxis: {
    tickAmount: 6,
    labels: {
      enabled: true,
    },
  },
  legend: {
    enabled: true,
  },
  ...beautifulTooltip((value) => numberWithCommas(toFixed(value, 2)), true),
});

export default function TravaTokenOrigin({ isEmpty, onBuyTRAVAClick }) {
  const cls = useStyle();

  const [isShowLock, setShowLock] = useState(true);
  const { address: accountAddress } = useWeb3Connect();

  const rTrava = useRTravaBalance(accountAddress);
  const rTravaBalance = rTrava.bscRTrava + rTrava.ethRTrava + rTrava.ftmRTrava;

  const nftTravaBalance = useNFTBalance(accountAddress);
  const vaultReward = useVaultRewardBalance(accountAddress);
  const stakedVaultBalance =
    vaultReward.stakedBalance.bscStakedTrava +
    vaultReward.stakedBalance.ethStakedTrava +
    vaultReward.stakedBalance.ftmStakedTrava;
  const vestingVaultBalance =
    vaultReward.vestingBalance.bscVestingTrava +
    vaultReward.vestingBalance.ethVestingTrava +
    vaultReward.vestingBalance.ftmVestingTrava;
  const travaUnclaim = nftTravaBalance + stakedVaultBalance + vestingVaultBalance;

  const travaToken = useReputationSelector((state) => state.reputationUser?.travaToken);

  const { series, categories } = useReputationSelector((state) => {
    let trava = [];
    let rTrava = [];
    let farmingTrava = [];
    let farmingRTrava = [];
    let categories = [];
    if (state.reputationUser) {
      Object.entries(state.reputationUser.balanceDetailHistory).forEach(([timestamp, item]) => {
        const time = parseInt(timestamp) * 1000;
        trava.push([item.TRAVA]);
        rTrava.push([item.RTRAVA]);
        farmingTrava.push([item.FARMING_TRAVA]);
        farmingRTrava.push([item.FARMING_RTRAVA]);
        categories.push(time);
      });
    }
    return {
      series: [
        { name: "TRAVA", data: trava },
        { name: "rTRAVA", data: rTrava },
        { name: "Farming TRAVA", data: farmingTrava },
        { name: "Farming rTRAVA", data: farmingRTrava },
      ],
      categories: categories,
    };
  });

  return (
    <PaperWrapper
      title="TRAVA Token Origin"
      onMouseEnter={() => setShowLock(false)}
      onMouseLeave={() => setShowLock(true)}
      paperProps={{ className: cls.root }}
    >
      <Box width="100%" height="100%" className={(clsx, { [cls.fogContent]: isEmpty })}>
        {travaToken && isNumeric(rTravaBalance) && isNumeric(travaUnclaim) ? (
          <Box>
            <StackChart series={series} option={getOption()} categories={categories} type={STAKING_TYPE.NORMAL} />
            <Box>
              <Typography color="textSecondary">
                You have <span className={cls.balance}>{numberWithCommas(toFixed(travaToken.TRAVA, 2))} </span>TRAVA ;{" "}
                <span className={cls.balance}>{numberWithCommas(toFixed(travaToken.RTRAVA, 2))}</span> rTRAVA;{" "}
                <span className={cls.balance}>{numberWithCommas(toFixed(travaToken.FARMING_TRAVA, 2))}</span> Farming
                TRAVA; <span className={cls.balance}>{numberWithCommas(toFixed(travaToken.FARMING_RTRAVA, 2))}</span>{" "}
                Farming rTRAVA (cross-chain)
              </Typography>
              <Typography color="textSecondary">
                You have <span className={cls.balance}>{numberWithCommas(toFixed(rTravaBalance, 2))}</span> rTRAVA and{" "}
                <span className={cls.balance}>{numberWithCommas(toFixed(travaUnclaim, 2))}</span> TRAVA unclaimed
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="Please connect your wallet first!" />
          </Box>
        )}
      </Box>
      {isEmpty && <LockHoverButton isShowLock={isShowLock} onBuyTRAVAClick={onBuyTRAVAClick} />}
    </PaperWrapper>
  );
}
